import React, { useRef, useState } from 'react';
import toast from 'react-hot-toast';
import { Image as ImageIcon, Trash2, Smartphone, Monitor } from 'lucide-react';
import { adminEnquetesAPI } from '../../utils/adminEnquetesAPI';
import { sucessoComHorario } from '../../utils/toastComHorario';
import MetadataImagem from './MetadataImagem';

const API_ORIGIN = process.env.NODE_ENV === 'production'
  ? ''
  : (process.env.REACT_APP_API_URL || 'http://localhost:3001/api/v1').replace(/\/api\/v1$/, '');

// Um bloco de upload — desktop e mobile empilhados (um embaixo do outro),
// não lado a lado: cada prévia fica com a largura toda do modal só pra ela,
// mais fácil de ver os dois recortes de perto sem precisar espremer os dois
// numa coluna estreita.
const BlocoUpload = ({ titulo, icone: Icone, ajuda, imagem, metadata, enviar, remover }) => {
  const inputRef = useRef(null);
  const [enviando, setEnviando] = useState(false);

  const selecionarArquivo = () => inputRef.current?.click();

  const onArquivoSelecionado = async (e) => {
    const arquivo = e.target.files?.[0];
    if (!arquivo) return;

    setEnviando(true);
    try {
      await enviar(arquivo);
      sucessoComHorario(`Imagem de capa (${titulo.toLowerCase()}) enviada`);
    } catch (error) {
      toast.error(error.userMessage || 'Erro ao enviar imagem');
    } finally {
      setEnviando(false);
      e.target.value = '';
    }
  };

  const onRemover = async () => {
    try {
      await remover();
      sucessoComHorario(`Imagem de capa (${titulo.toLowerCase()}) removida`);
    } catch (error) {
      toast.error(error.userMessage || 'Erro ao remover imagem');
    }
  };

  return (
    <div>
      <p className="text-xs font-semibold uppercase tracking-wide text-gray-400 mb-1.5 flex items-center gap-1.5">
        <Icone className="w-3.5 h-3.5" /> {titulo}
      </p>
      <p className="text-xs text-gray-400 mb-3">{ajuda}</p>
      <input
        ref={inputRef}
        type="file"
        accept="image/jpeg,image/png,image/webp"
        className="hidden"
        onChange={onArquivoSelecionado}
      />

      {imagem ? (
        <div className="space-y-3">
          <div>
            <img
              src={`${API_ORIGIN}${imagem}`}
              alt={`Capa (${titulo.toLowerCase()})`}
              className="h-72 w-full object-cover rounded-lg border border-gray-200"
            />
            <MetadataImagem metadata={metadata} />
          </div>
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={selecionarArquivo}
              disabled={enviando}
              className="text-sm text-gray-600 hover:bg-gray-100 rounded-lg px-3 py-2 border border-gray-200 flex items-center gap-1.5"
            >
              <ImageIcon className="w-3.5 h-3.5" /> {enviando ? 'Enviando...' : 'Trocar imagem'}
            </button>
            <button onClick={onRemover} className="text-red-500 hover:bg-red-50 rounded-lg px-3 py-2 text-sm flex items-center gap-1.5">
              <Trash2 className="w-3.5 h-3.5" /> Remover
            </button>
          </div>
        </div>
      ) : (
        <button
          type="button"
          onClick={selecionarArquivo}
          disabled={enviando}
          className="w-full h-32 text-sm text-gray-500 hover:text-primary-600 hover:border-primary-300 flex flex-col items-center justify-center gap-2 border border-dashed border-gray-300 rounded-lg transition-colors"
        >
          <ImageIcon className="w-5 h-5" /> {enviando ? 'Enviando...' : `Adicionar (${titulo.toLowerCase()})`}
        </button>
      )}
    </div>
  );
};

// Imagem de capa da enquete — vira o fundo do card na página inicial e o
// banner no topo do formulário público (ver EnquetePollCard.js e
// FormularioDinamico.js). A variante mobile é opcional: só entra em uso
// quando enviada, sempre caindo de volta pra capa "normal" em qualquer
// tamanho de tela quando não existe — enquete antiga com só uma capa
// continua funcionando exatamente igual.
const UploadImagemCapa = ({ enqueteId, imagemCapa, metadataImagemCapa, imagemCapaMobile, metadataImagemMobile, onChange }) => (
  <div className="space-y-8">
    <BlocoUpload
      titulo="Desktop"
      icone={Monitor}
      ajuda="Capa padrão — usada em telas largas. Formato paisagem (ex: 1600×600px)."
      imagem={imagemCapa}
      metadata={metadataImagemCapa}
      enviar={async (arquivo) => {
        await adminEnquetesAPI.enviarImagemCapa(enqueteId, arquivo);
        onChange();
      }}
      remover={async () => {
        await adminEnquetesAPI.removerImagemCapa(enqueteId);
        onChange();
      }}
    />
    <div className="border-t border-gray-100 pt-8">
    <BlocoUpload
      titulo="Mobile"
      icone={Smartphone}
      ajuda="Opcional — recorte próprio pra tela pequena (ex: 900×650px). Sem ela, usa a de desktop também no celular."
      imagem={imagemCapaMobile}
      metadata={metadataImagemMobile}
      enviar={async (arquivo) => {
        await adminEnquetesAPI.enviarImagemCapaMobile(enqueteId, arquivo);
        onChange();
      }}
      remover={async () => {
        await adminEnquetesAPI.removerImagemCapaMobile(enqueteId);
        onChange();
      }}
    />
    </div>
  </div>
);

export default UploadImagemCapa;
